import Enterprise24 from '@carbon/icons-react/es/enterprise/24';
import { ICellRendererParams } from 'ag-grid-community';
import { Space } from 'antd';
import { Dictionary } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { Tag } from 'components/atoms/tag';
import { getProjectDetailUrl } from 'utils/urls';
import { TimesheetStatus, UserLevelSummary } from '../../types';

const StyledLink = styled(Link)({
  color: 'initial',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    color: 'initial',
  },
});

const TagStyled = styled(Tag)`
  position: absolute;
  right: var(--spacing-05);

  span {
    margin-right: var(--spacing-02);
  }
`;

const NoPendingWrapper = styled.div`
  position: absolute;
  right: var(--spacing-05);
  color: var(--gray-40);
`;

const ApprovalPendingTag = ({ noOfApprovalPending }) => (
  <>
    {noOfApprovalPending > 0 ? (
      <TagStyled color="cyan">
        <span>{noOfApprovalPending}</span>
        {noOfApprovalPending > 1 ? (
          <FormattedMessage id="timesheet.approvals.pending" />
        ) : (
          <FormattedMessage id="timesheet.approval.pending" />
        )}
      </TagStyled>
    ) : (
      <NoPendingWrapper>
        <FormattedMessage id="timesheet.approval.no.pending" />
      </NoPendingWrapper>
    )}
  </>
);

const NON_PROJECT_ACTIVITY_ID = -1;

type CellRendererParamtypes = {
  projectsMap: Dictionary<Project>;
  userTimesheets: UserLevelSummary[];
  isUserAdmin: boolean;
  isWeekFreezed: boolean;
};

export const ProjectNameRenderer = (
  params: ICellRendererParams & CellRendererParamtypes,
) => {
  const { value, projectsMap, userTimesheets, isUserAdmin, isWeekFreezed } =
    params;

  const noOfApprovalPending = useMemo(() => {
    if (isWeekFreezed && !isUserAdmin) {
      return 0;
    }
    const usersInProject = userTimesheets?.filter(
      (user) => user.projectId === parseInt(value, 10),
    );

    return usersInProject.reduce((total, user) => {
      if (user.status === TimesheetStatus.Submitted) {
        return total + 1;
      }

      return total;
    }, 0);
  }, [isUserAdmin, isWeekFreezed, userTimesheets, value]);

  const projectData = projectsMap ? projectsMap[parseInt(value, 10)] : null;

  if (projectData) {
    return (
      <>
        <StyledLink to={getProjectDetailUrl(ProjectId(projectData.projectId))}>
          <Space size="small">
            <Avatar
              src={projectData.customer.companyLogoUrl}
              name={projectData.customer.companyName}
            />
            <div>{projectData.projectName}</div>
          </Space>
        </StyledLink>
        <ApprovalPendingTag noOfApprovalPending={noOfApprovalPending} />
      </>
    );
  }

  if (parseInt(value, 10) === NON_PROJECT_ACTIVITY_ID) {
    return (
      <>
        <Space size="small">
          <Enterprise24 />
          <div>
            <FormattedMessage id="timesheets.labels.non_project_activity" />
          </div>
        </Space>
        <ApprovalPendingTag noOfApprovalPending={noOfApprovalPending} />
      </>
    );
  }

  return null;
};
